import { Editor } from '@tiptap/react';
import { SendIcon } from 'lucide-react';
import React from 'react';

import { Button } from '../ui/button';

import { type MarkdownEditorProps, MarkdownEditor } from './markdown-editor';

import { cn } from '@/lib/utils';

interface CommentEditorProps
  extends Omit<MarkdownEditorProps, 'showStaticMenu' | 'showBubbleMenu' | 'showFloatingMenu' | 'onBlur'> {
  isExpanded?: boolean;
  onSubmit?: () => void;
  onBlur?: () => void;
}

export const CommentEditor = React.forwardRef<Editor, CommentEditorProps>(
  ({ value, placeholder = 'Add a comment...', isExpanded = true, onSubmit, className, onBlur, ...props }, ref) => {
    return (
      <div
        className={cn(
          'relative rounded-md border border-input bg-background px-2.5 transition-all duration-200 py-0.5',
          isExpanded ? 'min-h-[80px]' : 'min-h-[28px]',
          className
        )}
        onBlur={onBlur}
        onFocus={props.onFocus}
        onKeyDown={(e) => {
          // Submit on Cmd+Enter (Mac) or Ctrl+Enter
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (value.trim() && onSubmit) {
              onSubmit();
            }
          }
        }}
      >
        <MarkdownEditor ref={ref} value={value} placeholder={placeholder} showStaticMenu={isExpanded} {...props} />

        {/* Only show the send button when not readonly and either focused or has content */}
        {isExpanded && (
          <Button
            className="absolute bottom-2 right-2 rounded-full p-0 size-7 shrink-0"
            disabled={!value?.trim()}
            onMouseDown={(e) => {
              // Prevent the button click from triggering blur
              e.preventDefault();
            }}
            onClick={(e) => {
              e.stopPropagation();
              onSubmit?.();
            }}
            size="sm"
          >
            <SendIcon className="size-3 mr-[1.2px]" />
          </Button>
        )}
      </div>
    );
  }
);
